/**
 * Cloud access for `user_preferences` — the single JSONB settings blob per user.
 *
 * Eight individual localStorage keys collapse into ONE row here. Because those
 * keys are stored in localStorage as raw STRINGS (e.g. '1' / '0', a stringified
 * number, 'true'), each preference defines an `encode` (raw localStorage string
 * -> typed blob value) and `decode` (typed blob value -> raw localStorage string)
 * pair so a later sync chunk can move values in both directions losslessly. These
 * encoders mirror the exact parse/serialize logic already used by the app
 * (CashOutGoalCard, TaxView, useSampleMode, ThemeContext, CurrencyContext, ...).
 */
import type { UserPreferencesRow } from './rows';
import { supabase } from '../supabaseClient';
import { assertNoError } from './client';

const TABLE = 'user_preferences';

/** The typed shape of the `preferences` JSONB blob. All keys optional. */
export interface AppPreferences {
  theme?: 'dark' | 'light'; // <- mycryptoplanner_theme
  currency?: string; // <- mycryptoplanner_currency
  chartView?: 'line' | 'candle'; // <- mycryptoplanner_chart_view
  holdingsWalletFilter?: string; // <- mycryptoplanner_holdings_wallet_filter
  cashOutGoal?: number | null; // <- cashOutGoal (USD)
  goalShowProjection?: boolean; // <- goalShowProjection
  cgtBracketIdx?: number | null; // <- cgtBracketIdx
  sampleDataDismissed?: boolean; // <- sampleDataDismissed
}

/**
 * Per-preference codec. `prefKey` is the field inside the AppPreferences blob.
 * `encode` turns a raw localStorage string (or null when the key is absent) into
 * the typed value, returning `undefined` to mean "leave this key out of the
 * blob". `decode` turns a typed blob value back into the raw localStorage string,
 * returning `null` to mean "remove this localStorage key".
 */
export interface PreferenceCodec {
  prefKey: keyof AppPreferences;
  encode: (raw: string | null) => unknown;
  decode: (value: unknown) => string | null;
}

const numberOrNull = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

/** Codecs keyed by localStorage key. Faithful to each call-site's own logic. */
export const PREFERENCE_CODECS: Record<string, PreferenceCodec> = {
  // ThemeContext: stored verbatim; only 'light'/'dark' are meaningful.
  mycryptoplanner_theme: {
    prefKey: 'theme',
    encode: (raw) => (raw == null ? undefined : raw === 'light' ? 'light' : 'dark'),
    decode: (v) => (v === 'light' || v === 'dark' ? v : null),
  },
  // CurrencyContext: a plain currency code string.
  mycryptoplanner_currency: {
    prefKey: 'currency',
    encode: (raw) => (raw == null ? undefined : raw),
    decode: (v) => (typeof v === 'string' ? v : null),
  },
  // CoinChartPage: 'line' | 'candle'.
  mycryptoplanner_chart_view: {
    prefKey: 'chartView',
    encode: (raw) => (raw == null ? undefined : raw === 'candle' ? 'candle' : 'line'),
    decode: (v) => (v === 'candle' || v === 'line' ? v : null),
  },
  // HoldingsTable: an opaque wallet-filter string.
  mycryptoplanner_holdings_wallet_filter: {
    prefKey: 'holdingsWalletFilter',
    encode: (raw) => (raw == null ? undefined : raw),
    decode: (v) => (typeof v === 'string' ? v : null),
  },
  // CashOutGoalCard.readGoal: parseFloat, kept only when finite and > 0.
  cashOutGoal: {
    prefKey: 'cashOutGoal',
    encode: (raw) => {
      if (raw == null) return undefined;
      const n = parseFloat(raw);
      return isFinite(n) && n > 0 ? n : null;
    },
    decode: (v) => (numberOrNull(v) && v > 0 ? String(v) : null),
  },
  // CashOutGoalCard.readProj: '1' (true) / anything else (false).
  goalShowProjection: {
    prefKey: 'goalShowProjection',
    encode: (raw) => (raw == null ? undefined : raw === '1'),
    decode: (v) => (v === true ? '1' : v === false ? '0' : null),
  },
  // TaxView.readBracketIdx: parseInt; stored as the integer index.
  cgtBracketIdx: {
    prefKey: 'cgtBracketIdx',
    encode: (raw) => {
      if (raw == null) return undefined;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : null;
    },
    decode: (v) => (numberOrNull(v) ? String(v) : null),
  },
  // useSampleMode.isSampleDismissed: 'true' only ever written when dismissed.
  sampleDataDismissed: {
    prefKey: 'sampleDataDismissed',
    encode: (raw) => (raw == null ? undefined : raw === 'true'),
    decode: (v) => (v === true ? 'true' : null),
  },
};

export function toRow(prefs: AppPreferences, userId: string): UserPreferencesRow {
  return {
    user_id: userId,
    preferences: prefs as Record<string, unknown>,
  };
}

export function fromRow(row: UserPreferencesRow): AppPreferences {
  return (row.preferences ?? {}) as AppPreferences;
}

/** Fetch the user's preferences blob, or null when no row exists yet. */
async function fetch(userId: string): Promise<AppPreferences | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  assertNoError(error, `fetch(${TABLE})`);
  return data ? fromRow(data as UserPreferencesRow) : null;
}

async function upsert(userId: string, prefs: AppPreferences): Promise<void> {
  const { error } = await supabase
    .from(TABLE)
    .upsert(toRow(prefs, userId) as any, { onConflict: 'user_id' });
  assertNoError(error, `upsert(${TABLE})`);
}

async function remove(userId: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq('user_id', userId);
  assertNoError(error, `remove(${TABLE})`);
}

export const userPreferencesTable = {
  toRow,
  fromRow,
  fetch,
  upsert,
  remove,
};
